export function index() {
  return absolute([]);
}

export function auth(provider: string) {
  return absolute(["app", "auth", provider]);
}

export function login(redirectUrl?: string) {
  const accountPortalUrl =
    typeof window !== "undefined"
      ? window.PARROT_CONFIG?.CLERK_ACCOUNT_PORTAL_URL
      : process.env.CLERK_ACCOUNT_PORTAL_URL;

  return (
    accountPortalUrl +
    absolute(["sign-in"], redirectUrl ? { redirect_url: redirectUrl } : {})
  );
}

export function app() {
  return absolute(["app"]);
}

export function organizationNew() {
  return absolute(["organization", "new"]);
}

export function project(projectId: string) {
  return absolute(["app", "projects", projectId]);
}

export function projectDocPdf(projectId: string, docId: string) {
  return absolute(["app", "projects", projectId, "doc", docId, "document.pdf"]);
}

export function universalDocPdf(docId: string) {
  return absolute(["admin", "universal-docs", docId, "document.pdf"]);
}

export function organizationDocPdf(docId: string) {
  return absolute(["app", "organization-docs", docId, "document.pdf"]);
}

export function projectGetUploadUrl(projectId: string) {
  return absolute(["app", "projects", projectId, "upload-url"]);
}

export function universalDocsGetUploadUrl() {
  return absolute(["admin", "universal-docs", "upload-url"]);
}

export function adminOrganizationDocs() {
  return absolute(["admin", "organization-docs"]);
}

export function adminOrganizationDocsGetUploadUrl() {
  return absolute(["admin", "organization-docs", "upload-url"]);
}

export function adminOrganizationDocPdf(docId: string) {
  return absolute(["admin", "organization-docs", docId, "document.pdf"]);
}

export function organizationDocs() {
  return absolute(["app", "organization-docs"]);
}

export function organizationDocsGetUploadUrl() {
  return absolute(["app", "organization-docs", "upload-url"]);
}

export function projectDoc(docId: string) {
  return relative([], { docId });
}

export function projectDocMetadata(projectId: string, docId: string) {
  return absolute(["app", "projects", projectId, "doc", docId, "metadata"]);
}

export function projectNew() {
  return absolute(["app", "projects", "new"]);
}

export function projectTopicNew(projectId: string) {
  return absolute(["app", "projects", projectId, "topics", "new"]);
}

export function projectTopic(projectId: string, topicId: string) {
  return absolute(["app", "projects", projectId, "topics", topicId]);
}

export function projectAnswerStream(
  id: string,
  topicId: string,
  question: string,
  reasoningEffort?: "none" | "medium" | "high",
  verbosity?: "low" | "medium" | "high",
  useWebSearch?: boolean,
  useGoogleDrive?: boolean,
  useOneDrive?: boolean,
  useSources?: boolean,
  sourceTools?: string[]
) {
  const encodedQuestion = encodeURIComponent(question);
  const queryParams: Record<string, string | number | boolean> = {
    question: encodedQuestion,
  };

  if (reasoningEffort) {
    queryParams.reasoningEffort = reasoningEffort;
  }
  if (verbosity) {
    queryParams.verbosity = verbosity;
  }
  if (useWebSearch !== undefined) {
    queryParams.useWebSearch = useWebSearch;
  }
  if (useGoogleDrive !== undefined) {
    queryParams.useGoogleDrive = useGoogleDrive;
  }
  if (useOneDrive !== undefined) {
    queryParams.useOneDrive = useOneDrive;
  }
  if (useSources !== undefined) {
    queryParams.useSources = useSources;
  }
  if (sourceTools && sourceTools.length > 0) {
    queryParams.sourceTools = sourceTools.join(",");
  }

  return absolute(
    ["app", "projects", id, "topics", topicId, "stream"],
    queryParams
  );
}

export function logout() {
  return absolute(["logout"]);
}

export function googleDriveConnect() {
  return absolute(["api", "google-drive", "connect"]);
}

export function googleDriveCallback() {
  return absolute(["api", "google-drive", "callback"]);
}

export function googleDriveDisconnect() {
  return absolute(["api", "google-drive", "disconnect"]);
}

export function googleDriveStatus() {
  return absolute(["api", "google-drive", "status"]);
}

export function oneDriveConnect() {
  return absolute(["api", "onedrive", "connect"]);
}

export function oneDriveCallback() {
  return absolute(["api", "onedrive", "callback"]);
}

export function oneDriveDisconnect() {
  return absolute(["api", "onedrive", "disconnect"]);
}

export function oneDriveStatus() {
  return absolute(["api", "onedrive", "status"]);
}

export function privacyPolicy() {
  return absolute(["privacy-policy"]);
}

export function termsOfService() {
  return absolute(["terms-of-service"]);
}

export function leaflet() {
  return absolute(["leaflet"]);
}

export function transcriptions() {
  return absolute(["app", "transcriptions"]);
}

export function transcription(transcriptionId: string) {
  return absolute(["app", "transcriptions", transcriptionId]);
}

export function transcriptionsGetUploadUrl() {
  return absolute(["app", "transcriptions", "upload-url"]);
}

export function stats() {
  return absolute(["app", "stats"]);
}

export function googleOAuth() {
  return absolute(["app", "google-oauth"]);
}

export function microsoftOAuth() {
  return absolute(["app", "microsoft-oauth"]);
}

export function eli() {
  return absolute(["app", "eli"]);
}

export function eliPublisher(code: string, year?: number) {
  const params: Record<string, string | number> = {};
  if (year !== undefined) {
    params.year = year;
  }
  return absolute(["app", "eli", code], params);
}

export function eliAct(code: string, year: number, pos: number) {
  return absolute(["app", "eli", code, String(year), String(pos)]);
}

/**
 * Constructs a relative URL from the given path segments and query parameters.
 *
 * @param pathSegments - An array of strings representing the path segments to be joined.
 * @param queryParams - An object where keys and values represent the query parameters to be appended.
 * @returns A string representing the constructed URL.
 */
export const relative = (
  pathSegments: string[] = [],
  queryParams: Record<string, string | number | boolean> = {}
): string => {
  const path = pathSegments
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  const queryString = Object.entries(queryParams)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join("&");

  const url = `${path}${queryString ? "?" + queryString : ""}`;

  return url;
};

/**
 * Constructs an absolute URL from the given path segments and query parameters.
 *
 * @param pathSegments - An array of strings representing the path segments to be joined.
 * @param queryParams - An object where keys and values represent the query parameters to be appended.
 * @returns A string representing the constructed URL.
 */
export const absolute = (
  pathSegments: string[] = [],
  queryParams: Record<string, string | number | boolean> = {}
): string => {
  return `/${relative(pathSegments, queryParams)}`;
};
